import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Paper,
} from "@mui/material";
import isGHMC from "../helper/helpar";

const MUITable = ({ needSRNumber = true, columns, rows, tableHeight = 400, hasTotalRow = false }) => {
    const defaultRowsPerPage = 100;
    const [sortConfig, setSortConfig] = useState({ field: null, direction: "asc" });
    const [currentPage, setCurrentPage] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const isProd = isGHMC();

    // Handle sorting
    const handleSort = (field) => {
        const isAsc = sortConfig.field === field && sortConfig.direction === "asc";
        setSortConfig({ field, direction: isAsc ? "desc" : "asc" });
    };

    useEffect(() => {
        setCurrentPage(0)
    }, [rows])

    // Sort rows dynamically based on the current sort field
    const sortedRows = React.useMemo(() => {
        if (!sortConfig.field) return rows;
        return [...rows].sort((a, b) => {
            const aValue = a[sortConfig.field];
            const bValue = b[sortConfig.field];

            // Handle string comparison case-insensitively
            if (typeof aValue === "string" && typeof bValue === "string") {
                return sortConfig.direction === "asc"
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            // Handle number comparison
            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
            }

            // Fallback for other types
            return 0;
        });
    }, [rows, sortConfig]);

    // Paginate rows
    const paginatedRows = sortedRows?.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
    );

    // Handle page change
    const handleChangePage = (event, newPage) => {
        const lastPage = rows?.length / rowsPerPage
        setIsLastPage(Math.floor(lastPage) == newPage)
        setCurrentPage(newPage);
    };

    useEffect(() => {
        const lastPage = rows?.length / rowsPerPage;
        setIsLastPage(Math.floor(lastPage) == 0);
    }, [rows] )

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };
    const headerStyles = { textAlign: 'center', border: '1px solid #ddd', borderRight: 'none', padding: '10px', color: '#fff', backgroundColor: isProd ? "#0d5da5" : "#EC1117", }
    const cellStyles = { textAlign: 'center', border: '1px solid #ddd', borderRight: 'none', padding: '10px', whiteSpace: "normal" };

    return (
        <Paper>
            <TableContainer component={Paper}
                className={hasTotalRow ? "MUITable-hasTotalRow" : ""}
                sx={{
                    width: '100%',
                    maxHeight: tableHeight,
                    border: '1px solid #ccc',
                }}
            >
                <Table stickyHeader>
                    <TableHead className={`${isProd ? 'bg-ghmc' : 'bg-swm'}`}>
                        <TableRow>
                            {needSRNumber && <TableCell sx={headerStyles}>
                                S.No
                            </TableCell>}
                            {columns.map((column, columnIndex) => (
                                <TableCell
                                    sx={headerStyles}
                                    key={columnIndex}
                                    align={column.align || "left"}
                                    style={{ minWidth: column.width || "auto" }}
                                >
                                    {column.isSortable ? (
                                        <TableSortLabel
                                            active={sortConfig.field === column.field}
                                            direction={sortConfig.field === column.field ? sortConfig.direction : "asc"}
                                            onClick={() => handleSort(column.field)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRows?.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    No data available
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedRows?.map((row, rowIndex) => (
                                <TableRow key={rowIndex}
                                    sx={{
                                        backgroundColor: isProd
                                            ? rowIndex % 2 === 0
                                                ? '#8fc9ff'
                                                : '#aed8ff'
                                            : rowIndex % 2 === 0
                                                ? '#F8CCCD'
                                                : '#ffeded',
                                    }}
                                    className={isLastPage ? "Last-page" : ""}
                                >
                                    {needSRNumber && <TableCell sx={cellStyles}>
                                        <span>
                                            {currentPage * rowsPerPage + rowIndex + 1}
                                        </span>
                                    </TableCell>}
                                    {columns.map((column, columnIndex) => (
                                        <TableCell
                                            sx={cellStyles}
                                            key={columnIndex}
                                            align={column.align || "left"}
                                            style={{ minWidth: column.width || "auto" }}
                                        >
                                            {/* Handle dynamic fields with custom functions */}
                                            <span>
                                                {column.render
                                                    ? column.render(row, rowIndex)
                                                    : row[column.field]
                                                        ? row[column.field]
                                                        : "N/A"}
                                            </span>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[200, 500, 1000]}
                component="div"
                count={rows?.length}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
};

export default MUITable;
